import { z } from 'zod'
import {
  ApiCollectionResponseSchema,
  ApiPaginatedResponseSchema,
  ApiSingleResponseSchema,
} from './api'
import type {
  ApiCollectionResponse,
  ApiPaginatedResponse,
  ApiSingleResponse,
} from './api'

// Plan reference schema (for nested plan in subscription)
export const PlanReferenceSchema = z.object({
  id: z.number(),
  name: z.string(),
  short_name: z.string().optional(),
})

// Subscription Schema
export const SubscriptionSchema = z.object({
  id: z.number(),
  plan_id: z.number(),
  plan: PlanReferenceSchema.optional(),
  price_with_tax: z.coerce.number(), // Coerce string to number
  payment_type: z.enum(['bank_transfer', 'credit_card', 'paypal']),
  payment_frequency: z.enum(['monthly', 'quarterly', 'yearly']),
  invoicing_method: z.enum(['automatic', 'manual']),
  invoicing_option: z.enum(['e-invoice', 'fiscal']),
  payment_waiting_period: z.enum([
    'no-wait',
    'two-weeks',
    'monthly',
    '60_days',
  ]),
  started_at: z.string().optional(), // Make optional since server doesn't always send it
  expires_at: z.string().nullable(),
  is_active: z.boolean().optional(), // Make optional
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
})

export type Subscription = z.infer<typeof SubscriptionSchema>

// =====================================
// API RESPONSE SCHEMAS
// =====================================

// Single subscription response
export const SubscriptionApiResponseSchema =
  ApiSingleResponseSchema(SubscriptionSchema)
export type SubscriptionApiResponse = ApiSingleResponse<Subscription>

// Multiple subscriptions response
export const SubscriptionsApiResponseSchema =
  ApiCollectionResponseSchema(SubscriptionSchema)
export type SubscriptionsApiResponse = ApiCollectionResponse<Subscription>

// Paginated subscriptions response
export const SubscriptionsPaginatedApiResponseSchema =
  ApiPaginatedResponseSchema(SubscriptionSchema)
export type SubscriptionsPaginatedApiResponse =
  ApiPaginatedResponse<Subscription>

// =====================================
// CRUD SCHEMAS
// =====================================

export const CreateSubscriptionSchema = SubscriptionSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
  plan: true,
  is_active: true,
  started_at: true,
})

export type CreateSubscription = z.infer<typeof CreateSubscriptionSchema>

export const UpdateSubscriptionSchema = CreateSubscriptionSchema.partial()

export type UpdateSubscription = z.infer<typeof UpdateSubscriptionSchema>
